import React, {useMemo} from 'react';
import {Button, Checkbox, FormControlLabel} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import {OfficeIntegrationForm} from '../../api/Model';
import {useCurrentOfficeStore} from '../../api/CurrentOfficeStore';
import {useOfficeStore} from '../../api/OfficeStore';
import {useFormDialogAction} from '../../components/form-dialog/FormDialogProvider';
import {SetState, switchChange} from '../../util/FormHandlers';
import {useRemoteValue} from '../../util/RemoteValue';

function IntegrationSettingsForm({form, setForm}: {
    form: OfficeIntegrationForm,
    setForm: SetState<OfficeIntegrationForm>,
}) {
    const handleChange = useMemo(
        () => switchChange(setForm, enabled => ({checkInsIntegrationEnabled: enabled})),
        [setForm],
    );

    return (
        <FormControlLabel
            control={(
                <Checkbox
                    checked={form.checkInsIntegrationEnabled}
                    onChange={handleChange}
                />
            )}
            label="Check-Ins App integration is active"
        />
    );
}

export function ManageKeyIntegrationSettings() {
    const officeId = useCurrentOfficeStore(state => state.officeId);
    const {remoteValue, updateOfficeIntegrations} = useOfficeStore();
    const offices = useRemoteValue(remoteValue, []);
    const currentOffice = offices.find(office => office.id === officeId);

    const handleIntegrationSettings = useFormDialogAction({
        title: 'Integration Settings',
        description: 'Configure key integrations for the current office.',
        submitButtonText: 'Update',
        renderForm: IntegrationSettingsForm,
        action: openDialog => {
            if (!currentOffice) return;
            openDialog({
                initialValue: {
                    checkInsIntegrationEnabled: currentOffice.checkInsIntegrationEnabled,
                },
                onSubmit: form => updateOfficeIntegrations(
                    currentOffice,
                    form.checkInsIntegrationEnabled,
                ),
                successMessage: 'Updated integration settings.',
                errorMessage: 'Failed to update integration settings. Please try again later.',
            });
        },
    }, [currentOffice, updateOfficeIntegrations]);

    return (
        <Button
            variant="outlined"
            color="primary"
            startIcon={<SettingsIcon/>}
            onClick={handleIntegrationSettings}
            disabled={!currentOffice}
        >
            Integrations
        </Button>
    );
}
